import { useEffect, useState } from "react";
import { Editor } from "./editor/Editor";
import { AuthCallback } from "./auth/AuthCallback";
import { getAccessToken, login, logout } from "./auth/authClient";

export function App() {
  const [authed, setAuthed] = useState<boolean | null>(null);

  useEffect(() => {
    if (window.location.pathname === "/auth/callback") return;
    getAccessToken().then((t) => setAuthed(!!t));
  }, []);

  if (window.location.pathname === "/auth/callback") return <AuthCallback />;

  if (authed === null) {
    return (
      <div className="flex h-full items-center justify-center bg-slate-50">
        <div className="flex items-center gap-3 text-sm text-slate-500">
          <span className="h-2 w-2 animate-pulse rounded-full bg-tvs-red" />
          Cargando…
        </div>
      </div>
    );
  }

  if (!authed) return <LoginScreen />;

  return <Editor onLogout={() => logout()} />;
}

function LoginScreen() {
  return (
    <div className="grid h-full grid-cols-1 lg:grid-cols-5">
      {/* Brand panel */}
      <aside className="relative hidden overflow-hidden bg-gradient-to-br from-tvs-blue via-tvs-blue-dark to-[#061b4f] lg:col-span-3 lg:flex lg:flex-col lg:justify-between lg:p-12">
        {/* Decorative shapes */}
        <div className="pointer-events-none absolute -right-32 -top-32 h-96 w-96 rounded-full bg-tvs-red/20 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-40 -left-20 h-[28rem] w-[28rem] rounded-full bg-tvs-blue/40 blur-3xl" />
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.4) 1px, transparent 1px)",
            backgroundSize: "48px 48px",
          }}
        />

        <div className="relative flex items-center gap-3">
          <img src="/white-logo.png" alt="TVS Motor" className="h-10 w-auto" />
          <div className="border-l border-white/20 pl-3">
            <p className="text-sm font-semibold uppercase tracking-[0.2em] text-white/80">Mailings</p>
            <p className="text-xs text-white/60">Latin America · Marketing</p>
          </div>
        </div>

        <div className="relative max-w-xl">
          <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-medium uppercase tracking-wider text-white/80 ring-1 ring-inset ring-white/20">
            <span className="h-1.5 w-1.5 rounded-full bg-tvs-red" />
            SendGrid Dynamic Templates
          </span>
          <h2 className="mt-6 text-4xl font-semibold leading-tight text-white">
            Construye mailings de marca
            <br />
            <span className="text-tvs-red">en minutos, no en horas.</span>
          </h2>
          <p className="mt-5 text-base leading-relaxed text-white/70">
            Arrastra bloques pre-aprobados, inserta variables Handlebars y publica el template
            directo a SendGrid. Sin copiar HTML, sin romper estilos en Outlook.
          </p>

          <ul className="mt-8 space-y-3 text-sm text-white/80">
            <Feature text="9 bloques de marca listos para usar" />
            <Feature text="Plantillas pre-armadas (bienvenida, anuncios, confirmación)" />
            <Feature text="Variables de personalización integradas" />
            <Feature text="Compatible con Outlook, Gmail, Apple Mail" />
          </ul>
        </div>

        <div className="relative text-xs text-white/50">
          © {new Date().getFullYear()} TVS Motor Latin America · 18 mercados LATAM
        </div>
      </aside>

      {/* Login form panel */}
      <main className="flex items-center justify-center bg-slate-50 px-6 py-12 lg:col-span-2">
        <div className="w-full max-w-sm">
          {/* Mobile-only brand header */}
          <div className="mb-8 flex items-center gap-3 lg:hidden">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-tvs-blue p-1.5">
              <img src="/white-logo.png" alt="TVS Motor" className="h-full w-auto" />
            </div>
            <div>
              <p className="text-sm font-semibold text-tvs-blue">TVS Mailings</p>
              <p className="text-xs text-slate-500">Marketing · LATAM</p>
            </div>
          </div>

          <div>
            <h1 className="text-2xl font-semibold text-slate-900">Bienvenido de nuevo</h1>
            <p className="mt-2 text-sm text-slate-600">
              Inicia sesión con tu cuenta corporativa para acceder al constructor.
            </p>
          </div>

          <button
            type="button"
            onClick={() => login()}
            className="group mt-8 flex w-full items-center justify-center gap-3 rounded-lg bg-tvs-blue px-4 py-3 text-sm font-semibold text-white shadow-soft transition-all hover:-translate-y-0.5 hover:bg-tvs-blue-dark hover:shadow-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-tvs-red focus-visible:ring-offset-2"
          >
            <ShieldIcon />
            Continuar con Authentik
            <ArrowIcon />
          </button>

          <div className="mt-6 flex items-center gap-3 text-xs text-slate-400">
            <span className="h-px flex-1 bg-slate-200" />
            <span>Acceso seguro · OAuth2 + PKCE</span>
            <span className="h-px flex-1 bg-slate-200" />
          </div>

          <p className="mt-8 text-center text-xs text-slate-500">
            ¿Problemas para ingresar? Escribe a{" "}
            <a className="font-medium text-tvs-blue hover:text-tvs-red" href="mailto:felipe.rosas@tvslatinamerica.work">
              felipe.rosas@tvslatinamerica.work
            </a>
          </p>
        </div>
      </main>
    </div>
  );
}

function Feature({ text }: { text: string }) {
  return (
    <li className="flex items-start gap-3">
      <span className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-tvs-red/20 ring-1 ring-inset ring-tvs-red/40">
        <svg width="10" height="10" viewBox="0 0 12 12" fill="none" aria-hidden="true">
          <path d="M2 6.5L4.5 9L10 3.5" stroke="#EE2D3D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </span>
      <span>{text}</span>
    </li>
  );
}

function ShieldIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M12 2L4 5v6c0 5 3.5 9.5 8 11 4.5-1.5 8-6 8-11V5l-8-3z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ArrowIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      className="transition-transform group-hover:translate-x-1"
    >
      <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
